import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiChevronDown, FiSearch, FiCheck, FiImage } from 'react-icons/fi';

const VarietySelector = ({ varieties, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const dropdownRef = useRef(null);

    // Loose comparison for string vs number IDs
    const selectedVariety = varieties.find(v => v.id.toString() === value?.toString());

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (variety) => {
        onChange(variety.id.toString());
        setIsOpen(false);
        setFilter('');
    };

    const filteredVarieties = varieties.filter(v =>
        v.name.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full border rounded-lg p-2 bg-white flex items-center justify-between cursor-pointer transition-all ${isOpen ? 'border-[#16a34a] ring-2 ring-[#16a34a]/10' : 'border-gray-200 hover:border-[#16a34a]'}`}
            >
                {selectedVariety ? (
                    <div className="flex items-center space-x-3 overflow-hidden">
                        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-100 flex-shrink-0">
                            {selectedVariety.image ? (
                                <img src={selectedVariety.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <SafeIcon icon={FiImage} className="text-gray-400 text-xs" />
                            )}
                        </div>
                        <span className="text-sm font-medium text-gray-900 truncate">{selectedVariety.name}</span>
                    </div>
                ) : (
                    <span className="text-sm font-light text-gray-500 pl-1">Select Variety</span>
                )}
                <SafeIcon icon={FiChevronDown} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-100 rounded-xl shadow-xl max-h-96 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-gray-50 bg-gray-50/50">
                        <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-2 py-1.5 focus-within:border-[#16a34a] focus-within:ring-2 focus-within:ring-[#16a34a]/10 transition-all">
                            <SafeIcon icon={FiSearch} className="text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search variety..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="w-full outline-none text-xs font-light bg-transparent"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto p-1 custom-scrollbar">
                        {(() => {
                            if (filteredVarieties.length === 0) {
                                return <div className="p-4 text-center text-gray-400 text-xs font-light">No varieties found</div>;
                            }

                            // Group by Category
                            const grouped = filteredVarieties.reduce((acc, variety) => {
                                const cat = variety.category || 'Microgreens';
                                if (!acc[cat]) acc[cat] = [];
                                acc[cat].push(variety);
                                return acc;
                            }, {});

                            const sortedCategories = Object.keys(grouped).sort();

                            return sortedCategories.map(cat => (
                                <div key={cat}>
                                    <div className="px-2 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-50/50 mt-1 first:mt-0 rounded-md">
                                        {cat}
                                    </div>
                                    {grouped[cat].sort((a, b) => a.name.localeCompare(b.name)).map(variety => (
                                        <div
                                            key={variety.id}
                                            onClick={(e) => { e.stopPropagation(); handleSelect(variety); }}
                                            className={`grid grid-cols-[auto_1fr_auto] gap-3 p-2 rounded-lg cursor-pointer transition-colors items-center ${selectedVariety?.id === variety.id ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                                        >
                                            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-100 flex-shrink-0">
                                                {variety.image ? (
                                                    <img src={variety.image} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <SafeIcon icon={FiImage} className="text-gray-400 text-xs" />
                                                )}
                                            </div>
                                            <div className="min-w-0 pr-1">
                                                <p className={`text-sm truncate ${selectedVariety?.id === variety.id ? 'font-medium text-[#16a34a]' : 'font-light text-gray-900'}`}>{variety.name}</p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {selectedVariety?.id === variety.id && <SafeIcon icon={FiCheck} className="text-[#16a34a]" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VarietySelector;
